// Polyfork characters: realistic city and beach people. Riders, paddlers and the volleyball keep the Quaternius models.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { M } from '../../lib/factory.js';
import { rnd, rr } from '../../lib/random.js';
import { toonify } from '../../core/optimise.js';
import { walkers } from '../models.js';
import { PI } from '../../lib/math.js';
import { beachLie, sitSpots, standSpots, swimSpots, waders } from '../crowd.js';
import { H, idlers } from './loader.js';
import { build } from './outfits.js';
import { clipFromJson } from './polyfork-clip.js';

// people you would pass on a Barcelona pavement; `-f` files are feminine variants baked from the same outfit
const CITY = [
  'commuter',
  'commuter-f',
  'salaryman-commuter',
  'delivery-courier',
  'suburban-resident',
  'suburban-resident-f',
  'visitor-with-badge',
  'visitor-with-badge-f',
  'tour-guide',
  'backpacker',
  'street-skater',
  'professor',
  'student-with-backpack',
  'student-in-a-hoodie',
  'cafe-customer',
  'parent-chaperone',
  'passenger',
  'neighborhood-child',
];
// beach people with the robes, coats and shorts taken off; the men and kids are remixed pool guests with the painted
// bikini bottom grown into trunks, see public/assets/people/LICENSE.txt
const BEACH = [
  'beach-bikini',
  'beach-trunks',
  'beach-holidaymaker',
  'beach-trunks-old',
  'beach-lifeguard',
  'beach-bikini-old',
  'beach-kid-m',
  'beach-wetsuit',
  'beach-kid-f',
];
// the clips are licensed to play from Polyfork's CDN only, so they are fetched, never shipped
const CLIP_URL = (slug) => `https://polyfork.dev/cdn/anim-${slug}.json`;
const ADULT_M = 1.75; // a 1.75 m adult is H units tall, like the Quaternius people
const K = () => H / ADULT_M; // H is set when the Quaternius loader runs
const BAKED_WALK = 1.45; // m/s the Walk clip covers at timeScale 1, for the baked 0.95 m hip
const SEAT_HIP = 0.17; // m the seated Hips bone sits above sitSpots y (tuned by eye)
const LIE_HIP = 0.12; // m the Hips bone lies above the towel
const NECK_UP = 0.07; // m a treader's neck stays above the sea level at its lowest, clear of the waves
const SWIM_HIP = 0.14; // m a swimmer's hips ride under the surface: the back and the stroking arms break it
const KNEE_DROP = 0.4; // rad the thighs tilt down: the benches are taller than the chair the clips were baked on
// seated clips move the hips back onto the seat; standing ones play on the spot (Idle twice: it is the usual wait)
const SIT = ['sitting', 'sitting-talking'],
  STAND = ['idle', 'idle', 'phone-call', 'arms-folded', 'talking'];

// One skinned mesh per file. Every garment shares the skeleton; the parts are brought into the first part's space
// (the packed files quantize positions per mesh node) before merging.
function mergeCharacter(root) {
  root.updateMatrixWorld(true);
  const skinned = [];
  root.traverse((o) => o.isSkinnedMesh && skinned.push(o));
  const first = skinned[0],
    toFirst = new THREE.Matrix4(),
    nm = new THREE.Matrix3(),
    v = new THREE.Vector3(),
    parts = skinned.map((o) => {
      const src = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone(),
        n = src.attributes.position.count,
        g = new THREE.BufferGeometry();
      if (!src.attributes.normal) src.computeVertexNormals(); // flat-shaded parts can be packed without normals
      toFirst.copy(first.matrixWorld).invert().multiply(o.matrixWorld);
      nm.getNormalMatrix(toFirst);
      const out = (name, size, fn) => {
        const a = new Float32Array(n * size);
        for (let i = 0; i < n; i++) fn(src.attributes[name], i, a);
        g.setAttribute(name, new THREE.BufferAttribute(a, size));
      };
      // dequantized through getX, which honours the normalized flag
      out('position', 3, (s, i, a) =>
        a.set(v.fromBufferAttribute(s, i).applyMatrix4(toFirst).toArray(), i * 3),
      );
      out('normal', 3, (s, i, a) =>
        a.set(v.fromBufferAttribute(s, i).applyMatrix3(nm).normalize().toArray(), i * 3),
      );
      out('color', 3, (s, i, a) => a.set([s.getX(i), s.getY(i), s.getZ(i)], i * 3));
      out('skinWeight', 4, (s, i, a) => a.set([s.getX(i), s.getY(i), s.getZ(i), s.getW(i)], i * 4));
      const si = src.attributes.skinIndex,
        idx = new Uint16Array(n * 4);
      for (let i = 0; i < n; i++) idx.set([si.getX(i), si.getY(i), si.getZ(i), si.getW(i)], i * 4);
      g.setAttribute('skinIndex', new THREE.BufferAttribute(idx, 4));
      return g;
    });
  const mesh = new THREE.SkinnedMesh(
    mergeGeometries(parts),
    M(0xffffff, { vertexColors: true, flatShading: false, roughness: 0.7 }),
  );
  // the packed files keep their dequantization scale on the mesh node, so the merged mesh takes the first's transform
  mesh.position.copy(first.position);
  mesh.quaternion.copy(first.quaternion);
  mesh.scale.copy(first.scale);
  first.parent.add(mesh);
  mesh.bind(first.skeleton, first.bindMatrix);
  skinned.forEach((o) => o.parent.remove(o));
  toonify(mesh.material);
  mesh.castShadow = mesh.receiveShadow = true;
  mesh.frustumCulled = false; // bind-pose bounds do not follow the walk
  return root;
}

const clips = {},
  getClip = (slug) =>
    (clips[slug] ??= fetch(CLIP_URL(slug), { signal: AbortSignal.timeout(15000) }).then((r) => {
      if (!r.ok) throw new Error(`clip ${slug}: HTTP ${r.status}`);
      return r.json();
    }));

// The files in `names` as merged people with Walk and Idle clips, plus `extra` clip slugs fetched alongside.
async function loadSet(names, extra) {
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder),
    [walkRaw, idleRaw, more, ...gltfs] = await Promise.all([
      getClip('walk'),
      getClip('idle'),
      extra,
      ...names.map((f) => loader.loadAsync(`assets/people/${f}.glb`)),
    ]);
  return gltfs.map((gltf, i) => {
    const root = mergeCharacter(gltf.scene),
      hips = root.getObjectByName('Hips');
    if (!hips) throw new Error(`${names[i]}: no Hips bone`);
    const rest = hips.position.toArray();
    return {
      name: names[i],
      root,
      rest,
      more,
      walk: clipFromJson(walkRaw, rest),
      idle: clipFromJson(idleRaw, rest),
      hipY: rest[1],
    };
  });
}

export async function loadPolyforkPeople() {
  // a failed pose clip only keeps the Quaternius sitters and standers; the walkers still change
  const poses = Promise.all([...SIT, ...STAND.slice(2)].map(getClip)).catch((e) =>
    console.warn('city poses not loaded, keeping the sitters', e),
  );
  return (await loadSet(CITY, poses)).map((P) => {
    const pose = (raw, inPlace) => clipFromJson(raw, P.rest, { inPlace });
    P.sit = P.more?.slice(0, SIT.length).map((raw) => lowerKnees(pose(raw, false)));
    P.stand = P.more && [P.idle, P.idle, ...P.more.slice(SIT.length).map((raw) => pose(raw, true))];
    return P;
  });
}

// Beach people with Swimming and Treading water; any failure leaves the whole beach on Quaternius.
export async function loadPolyforkBeach() {
  return (await loadSet(BEACH, Promise.all(['swimming', 'treading-water'].map(getClip)))).map(
    (P) => {
      [P.swim, P.tread] = P.more.map((raw) => clipFromJson(raw, P.rest));
      return P;
    },
  );
}

// Swaps the sidewalk and promenade walkers (or, with `beach`, the strollers on the sand) onto Polyfork people.
export function dressPolyforkWalkers(people, beach = false) {
  // every replacement is built first, then swapped, so a failure leaves all the walkers as they were
  let n = 0;
  const swaps = [];
  walkers.forEach((w) => {
    if (!w.beach !== !beach || !w.model) return;
    const P = people[n++ % people.length],
      model = SkeletonUtils.clone(P.root);
    model.scale.setScalar(K());
    const mixer = new THREE.AnimationMixer(model),
      walkA = mixer.clipAction(P.walk),
      idleA = mixer.clipAction(P.idle);
    walkA.play();
    idleA.play();
    walkA.time = rnd() * P.walk.duration;
    idleA.time = rnd() * P.idle.duration;
    idleA.setEffectiveWeight(0);
    swaps.push([w, model, build()]); // heights and widths vary; the clothes already vary the figure
    w.next = {
      mixer,
      walkA,
      idleA,
      head: model.getObjectByName('Head'),
      // walking pace at timeScale 1, in world units/s, for this body's hip height
      speed: BAKED_WALK * (P.hipY / 0.95) * K(),
      cycle: P.walk.duration,
    };
  });
  for (const [w, model, [wd, ht]] of swaps) {
    w.g.clear();
    w.g.add(model);
    w.g.scale.set(wd, ht, wd);
    w.model = w.next;
    delete w.next;
  }
}

// Tilts both thighs down by KNEE_DROP and turns the shins back by as much, so the shins keep their world angle and
// the feet reach the ground from a taller seat.
function lowerKnees(clip) {
  const R = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), KNEE_DROP),
    Ri = R.clone().invert(),
    q = new THREE.Quaternion(),
    track = (n) => clip.tracks.find((t) => t.name === `${n}.quaternion`)?.values;
  for (const s of ['Left', 'Right']) {
    const up = track(`${s}UpLeg`),
      leg = track(`${s}Leg`);
    if (!up || !leg) continue;
    for (let i = 0; i < up.length; i += 4) q.fromArray(up, i).multiply(R).toArray(up, i);
    for (let i = 0; i < leg.length; i += 4) q.fromArray(leg, i).premultiply(Ri).toArray(leg, i);
  }
  return clip;
}

// A Polyfork person playing `clip` from a random point, not yet placed anywhere.
function make(P, clip, k) {
  const model = SkeletonUtils.clone(P.root);
  model.scale.setScalar(k);
  const mixer = new THREE.AnimationMixer(model),
    a = mixer.clipAction(clip);
  a.play();
  a.time = rnd() * clip.duration;
  mixer.update(0);
  return { model, mixer, a };
}

// Bench sitters and bus-stop standers become Polyfork people, in the same holders and spots. The Quaternius props
// go too: they were held by Quaternius arm IK. Paddlers keep their models.
export function placePolyforkSitters(people) {
  const adults = people.filter((P) => P.sit && !/child/.test(P.name));
  if (!adults.length) return;
  // every model is built before any swap, so a failure leaves the Quaternius sitters and standers in place
  const v = new THREE.Vector3(),
    sits = sitSpots.flatMap((sp, i) => {
      if (!sp.holder) return [];
      const P = adults[(i * 5 + 1) % adults.length];
      return [[sp, make(P, P.sit[Math.floor(rnd() * P.sit.length)], K() * rr(0.94, 1.04))]];
    }),
    stands = standSpots.flatMap((sp, i) => {
      if (sp.sup || !sp.model) return [];
      const P = adults[(i * 2 + 3) % adults.length];
      return [[sp, make(P, P.stand[Math.floor(rnd() * P.stand.length)], K())]];
    });
  for (const [sp, { model, mixer }] of sits) {
    const holder = sp.holder;
    holder.clear();
    holder.matrixWorldAutoUpdate = true; // the seated clips breathe and talk
    holder.add(model);
    idlers.push(mixer);
    holder.updateMatrixWorld(true);
    // the hips onto the seat (sitSpots y is the seat top less the hip depth), slightly in from the backrest
    const h = holder.worldToLocal(model.getObjectByName('Hips').getWorldPosition(v));
    model.position.set(-h.x, sp.y + SEAT_HIP * K() - h.y, 0.02 - h.z);
  }
  for (const [sp, { model, mixer }] of stands) {
    sp.model.removeFromParent();
    const old = idlers.indexOf(sp.mixer);
    if (old >= 0) idlers.splice(old, 1); // never splice(-1): that would drop someone else's mixer
    sp.parent.add(model);
    idlers.push(mixer);
    model.position.set(sp.x, sp.y, sp.z);
    model.rotation.y = sp.ry;
    sp.model = model;
    sp.mixer = mixer;
  }
}

// Replaces `old` with `next` in the idlers; never splice(-1), which would drop someone else's mixer.
function swapIdler(old, next) {
  const i = idlers.indexOf(old);
  if (i >= 0) idlers.splice(i, 1, next);
  else idlers.push(next);
}

// Turns `bone` by `a` about `axis` in its model's space (the model is upright and unrotated).
function turn(bone, axis, a) {
  const pq = bone.parent.getWorldQuaternion(new THREE.Quaternion());
  bone.quaternion.premultiply(
    pq.clone().invert().multiply(new THREE.Quaternion().setFromAxisAngle(axis, a)).multiply(pq),
  );
}

// Sunbathers, swimmers and waders become Polyfork people in the same holders and paths. Every one is built before
// any swap; the Quaternius towel props go (they were held by Quaternius bones).
export function placePolyforkBeach(people) {
  const of = (re) => people.filter((P) => re.test(P.name)),
    lie = of(/bikini|trunks/),
    swim = of(/bikini|trunks|kid|wetsuit/),
    wade = of(/bikini|trunks|kid|holidaymaker/),
    X = new THREE.Vector3(1, 0, 0),
    Y = new THREE.Vector3(0, 1, 0),
    v = new THREE.Vector3(),
    local = (holder, model, n) =>
      holder.worldToLocal(model.getObjectByName(n).getWorldPosition(v)).clone(),
    swaps = [];
  // a still from the Idle clip laid flat on the towel, on the back or face down, the head turned a little
  beachLie.forEach((t, i) => {
    if (!t.holder) return;
    const P = lie[(i * 3 + 1) % lie.length],
      { model } = make(P, P.idle, K() * rr(0.94, 1.04)),
      down = rnd() < 0.35;
    model.updateMatrixWorld(true);
    turn(model.getObjectByName('Head'), Y, rr(-0.6, 0.6));
    if (down) for (const s of ['Left', 'Right']) turn(model.getObjectByName(s + 'Foot'), X, 1.1); // toes back
    model.rotation.x = down ? PI / 2 : -PI / 2;
    swaps.push(() => {
      const holder = t.holder;
      holder.clear();
      holder.add(model);
      holder.updateMatrixWorld(true);
      const h = local(holder, model, 'Hips');
      model.position.set(-h.x, LIE_HIP * K() - h.y, -h.z);
      holder.updateMatrixWorld(true);
    });
  });
  // a third tread water where they are, head above the surface all stroke long; the others swim their laps
  swimSpots.forEach((sp, i) => {
    if (!sp.holder) return;
    const tread = i % 3 === 0,
      P = swim[(i * 5 + 2) % swim.length],
      clip = tread ? P.tread : P.swim,
      m = make(P, clip, K() * rr(0.94, 1.04));
    if (!tread) m.a.timeScale = rr(0.85, 1.15);
    swaps.push(() => {
      const holder = sp.holder;
      holder.clear();
      holder.add(m.model);
      swapIdler(sp.mixer, m.mixer);
      if (tread) sp.bob.rad = 0;
      let low = Infinity;
      for (let k = 0; k < 8; k++) {
        m.mixer.setTime((k / 8) * clip.duration);
        holder.updateMatrixWorld(true);
        low = Math.min(low, local(holder, m.model, tread ? 'Neck' : 'Hips').y);
      }
      m.mixer.setTime(rnd() * clip.duration);
      // the holder sits at the sea surface
      m.model.position.y = (tread ? NECK_UP : -SWIM_HIP) * K() - low;
    });
  });
  // waders: the Walk clip at their slow pace, on the same back-and-forth path
  waders.forEach((sp, i) => {
    if (!sp.holder) return;
    const P = wade[(i * 3 + 1) % wade.length],
      k = K() * rr(0.94, 1.04),
      m = make(P, P.walk, k);
    m.a.timeScale = sp.v / (BAKED_WALK * (P.hipY / 0.95) * k);
    swaps.push(() => {
      sp.holder.clear();
      sp.holder.add(m.model);
      swapIdler(sp.mixer, m.mixer);
    });
  });
  swaps.forEach((f) => f());
}
